import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UploadCloud, Loader2, Check, Sparkles, MessageCircle, Layers, PanelsTopLeft } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const CHUNK = 5 * 1024 * 1024;
const PERKS = [[Sparkles, "Hero start screen and cinematic intro"], [PanelsTopLeft, "Top menus, info windows and share"], [MessageCircle, "AI assistant that reads your PDF"], [Layers, "Switch every feature on and off"]];

async function uploadZip(file, demo, onPct) {
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append("token", demo.token); fd.append("index", i); fd.append("total", total); fd.append("size", file.size); fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "part.zip");
    for (let a = 0; ; a += 1) {
      try { await api.post(`/public/tdv-demo/${demo.id}/chunk`, fd, { timeout: 120000 }); break; }
      catch (e) { if (a >= 3 || e.response?.status === 400) throw e; await new Promise((r) => setTimeout(r, 2000 * (a + 1))); }
    }
    onPct(Math.round(((i + 1) / total) * 100));
  }
}

function useDemoUpload() {
  const nav = useNavigate();
  const [state, setState] = useState({ step: "idle", pct: 0, files: 0, error: "" });
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const start = async (file) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) { setState({ step: "idle", error: "Please choose the .zip of the folder 3DVista published." }); return; }
    setState({ step: "upload", pct: 0, error: "" });
    try {
      const { data: demo } = await api.post("/public/tdv-demo");
      localStorage.setItem(`tdv_demo_${demo.id}`, demo.token);
      await uploadZip(file, demo, (pct) => setState((s) => ({ ...s, pct })));
      setState((s) => ({ ...s, step: "process" }));
      timer.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/public/tdv-demo/${demo.id}`, { params: { token: demo.token } });
          if (data.status === "ready") { clearInterval(timer.current); nav(`/3d/${demo.id}`); }
          else if (data.status === "waiting" && data.error) { clearInterval(timer.current); setState({ step: "idle", error: data.error }); }
          else setState((s) => ({ ...s, files: data.files }));
        } catch (e) { clearInterval(timer.current); setState({ step: "idle", error: errMsg(e) }); }
      }, 2000);
    } catch (e) { setState({ step: "idle", error: errMsg(e) }); }
  };
  return [state, start];
}

function Drop({ state, start }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const busy = state.step !== "idle";
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); if (!busy) start(e.dataTransfer.files?.[0]); }}
      onClick={() => !busy && ref.current?.click()} className={`rounded-3xl border-2 border-dashed p-10 sm:p-14 text-center cursor-pointer transition-colors ${over ? "border-[#C9A227] bg-white/10" : "border-white/25 hover:border-white/50 bg-white/5"}`} data-testid="tdv-demo-drop">
      <input ref={ref} type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => start(e.target.files?.[0])} data-testid="tdv-demo-file" />
      {state.step === "idle" && <><UploadCloud className="w-10 h-10 mx-auto text-[#C9A227]" /><p className="mt-4 font-semibold text-lg">Drop your 3DVista build .zip here</p><p className="text-sm text-white/60 mt-1">or click to choose it · up to 1GB</p></>}
      {state.step === "upload" && <div data-testid="tdv-demo-uploading"><Loader2 className="w-10 h-10 mx-auto animate-spin text-[#C9A227]" /><p className="mt-4 font-semibold">Uploading · {state.pct}%</p><div className="h-1.5 rounded-full bg-white/15 mt-4 max-w-xs mx-auto overflow-hidden"><div className="h-full bg-[#C9A227] transition-[width]" style={{ width: `${state.pct}%` }} /></div></div>}
      {state.step === "process" && <div data-testid="tdv-demo-processing"><Loader2 className="w-10 h-10 mx-auto animate-spin text-[#C9A227]" /><p className="mt-4 font-semibold">Unpacking your tour{state.files ? ` · ${state.files.toLocaleString()} files` : ""}</p><p className="text-sm text-white/60 mt-1">Opens automatically when it's ready</p></div>}
      {state.error && <p className="mt-4 text-sm text-rose-300" data-testid="tdv-demo-error">{state.error}</p>}
    </div>
  );
}

export function OwnerBadge({ base = "/3dvista" }) {
  const [admin, setAdmin] = useState(null);
  useEffect(() => { api.get("/public/tdv-demo/me").then((r) => setAdmin(r.data.admin)).catch(() => setAdmin(false)); }, []);
  if (admin === null) return null;
  return admin
    ? <p className="mt-4 text-sm text-emerald-300 flex items-center gap-2" data-testid="tdv-demo-admin"><Check className="w-4 h-4" />You're signed in as the owner: no demo limits.</p>
    : <p className="mt-4 text-xs text-white/50" data-testid="tdv-demo-owner-login">Site owner? <Link to={`${base}/login?next=${base}/try`} className="underline">Log in</Link> for unlimited demos.</p>;
}

export default function TdvDemo() {
  const [state, start] = useDemoUpload();
  return (
    <div className="min-h-screen bg-[#0B1220] text-white" data-testid="tdv-demo-page">
      <header className="max-w-6xl mx-auto px-6 h-20 flex items-center"><Link to="/3dvista" className="font-serif text-xl">Showhome <span className="text-[#C9A227]">for 3DVista</span></Link></header>
      <main className="max-w-6xl mx-auto px-6 pt-8 pb-20 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#C9A227]">Free live demo · no sign-up</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">See your own tour with Showhome on top</h1>
          <p className="text-white/70 mt-5 max-w-lg">Upload the zipped folder 3DVista published. In a minute you'll see your tour with our skin, menus, info windows and AI assistant, all switched on. Nothing to install, and the demo deletes itself after 24 hours.</p>
          <ul className="mt-8 space-y-3">{PERKS.map(([I, t]) => <li key={t} className="flex items-center gap-3 text-sm"><span className="w-9 h-9 rounded-full bg-white/10 grid place-items-center"><I className="w-4 h-4 text-[#C9A227]" /></span>{t}</li>)}</ul>
        </div>
        <div>
          <Drop state={state} start={start} />
          <OwnerBadge />
          <ul className="mt-5 space-y-2 text-xs text-white/55">
            {["3 free demos per connection", "AI assistant capped at 5 questions per demo", "Demo links work for 24 hours, then the tour is deleted"].map((t) => <li key={t} className="flex gap-2"><Check className="w-3.5 h-3.5 text-[#C9A227] shrink-0" />{t}</li>)}
          </ul>
        </div>
      </main>
    </div>
  );
}
